import React from "react";
import ReactDOM from "react-dom";
import ReactTooltip from "react-tooltip";
import { getTooltipContent, observeTooltipContent } from "./tooltipRegistry";

/**
 * The `data-for` value every tooltip trigger points at. There is exactly one `ReactTooltip`
 * instance for the whole app, so a trigger identifies *which* tooltip it wants through
 * `data-tip` instead (see `tooltipRegistry`).
 */
export const SHARED_TOOLTIP_ID = "app-shared-tooltip";

const TRIGGER_SELECTOR = `[data-tip][data-for="${SHARED_TOOLTIP_ID}"]`;

/*
 * Per-trigger settings the call sites share. These go on the trigger element as
 * react-tooltip's own `data-place` attribute (see the note on `AppTooltip` below), or feed
 * the "is this actually cut off?" check a content callback makes before returning anything.
 */

/**
 * Placement for a tooltip a row owns, in any list or grid whose column headers sit directly
 * above the first row.
 *
 * The default upward placement puts the bubble over those headers, and a react-tooltip
 * bubble takes pointer events, so it also swallows what should have been a click on the
 * header — on the resizable table that means the column filter/search input becomes
 * unusable while a top-row tooltip is open. react-tooltip only overrides a per-trigger
 * `data-place` when the desired side does not fit in the viewport, and a row with the header
 * above it always has room below, so opening downwards keeps the header clear.
 *
 * Rows at the bottom edge of the viewport may still flip back to the top when the bubble is
 * too tall to fit below. That is harmless: the header is nowhere near them.
 */
export const ROW_TOOLTIP_PLACE = "bottom";

/**
 * Placement for the "?" / warning icons that sit just after a page or section heading.
 *
 * With `effect="solid"` and a top/bottom placement react-tooltip centres the bubble on the
 * trigger (`l: -(tipWidth / 2)`), and these bubbles run to the full `max-width: 40rem` that
 * `migration_gaia.scss` gives them, because the text is a paragraph of help. A heading icon
 * is only ~150px from the left edge of the content area, so a centred bubble reaches some
 * 320px further left than that — over the left navigation on the full-width configuration
 * pages. It is not even stable there: react-tooltip measures the overflow against the
 * window, so whether it renders above or gets pushed aside depends on how wide the menu
 * currently is.
 *
 * Opening to the right puts the bubble in the empty content area beside the heading, where
 * there is always room, and keeps the placement the same at every window size.
 */
export const HEADING_INFO_TOOLTIP_PLACE = "right";

/**
 * Slack for the "has this text been cut off?" test that gates a tooltip which only repeats a
 * cell or heading's own value. `scrollWidth` and `clientWidth` are rounded to whole pixels,
 * so content that fits exactly can still report a 1px overflow on a fractional layout, and
 * an overflow that small is invisible anyway.
 */
export const TRUNCATION_TOLERANCE_PX = 1;

/**
 * `bodyMode` delegates `mouseover`/`mouseout` on the body instead of binding per target, which
 * also means it never binds the `focus`/`blur` handlers the per-target mode gives keyboard
 * users. `focusin`/`focusout` bubble, so two delegated listeners can relay them onto the hover
 * path. We synthesise a mouse event rather than calling `ReactTooltip.show()`, because the
 * static show does a full-document walk to verify the target belongs to this instance.
 */
function useKeyboardTriggers() {
    React.useEffect(() => {
        const relay = (type) => (event) => {
            const trigger = event.target?.closest?.(TRIGGER_SELECTOR);

            if (trigger) {
                trigger.dispatchEvent(new MouseEvent(type, { bubbles: true }));
            }
        };

        const onFocusIn = relay("mouseover");
        const onFocusOut = relay("mouseout");

        document.addEventListener("focusin", onFocusIn);
        document.addEventListener("focusout", onFocusOut);

        return () => {
            document.removeEventListener("focusin", onFocusIn);
            document.removeEventListener("focusout", onFocusOut);
        };
    }, []);
}

/**
 * Some tooltips only fetch their text once hovered — the task list's comment cell starts the
 * request from `onMouseEnter` — so `getContent` still returns "" when the pointer arrives.
 * react-tooltip treats an empty tip as a disabled one and bails out of showing it (`updateTooltip`
 * in `dist/index.es.js`), and nothing re-triggers when the text lands, so the tooltip stayed
 * invisible until the pointer left and came back. The native `title` these replaced did not have
 * the problem: the browser reads the attribute when it draws the tooltip, roughly a second later.
 *
 * So when content is registered for the trigger currently under the pointer, ask react-tooltip to
 * show again and re-evaluate with the text now in place.
 *
 * This goes through `ReactTooltip.show`, which dispatches a window event, rather than replaying a
 * `mouseover` on the trigger: a synthetic mouse event bubbles to the React root, which re-fires the
 * cell's own `onMouseEnter`, which starts the fetch again — an endless fetch/re-register loop. The
 * static show does cost one full-document walk to verify the target belongs to this instance, but
 * it only runs when a hovered tooltip's content actually changes, so it is not on any hot path.
 */
function useHoverRefresh() {
    React.useEffect(() => {
        let hovered = null;
        let pending = false;

        const onMouseOver = (event) => {
            const trigger = event.target?.closest?.(TRIGGER_SELECTOR);

            if (trigger) {
                hovered = trigger;
            }
        };

        const onMouseOut = (event) => {
            const trigger = event.target?.closest?.(TRIGGER_SELECTOR);

            if (trigger && trigger === hovered && !trigger.contains(event.relatedTarget)) {
                hovered = null;
            }
        };

        const stopObserving = observeTooltipContent((id) => {
            if (pending || hovered?.getAttribute("data-tip") !== id) {
                return;
            }

            // Registration happens during render, so let React finish committing first. The flag
            // collapses a burst of registrations from one render into a single re-show.
            pending = true;
            Promise.resolve().then(() => {
                pending = false;

                if (hovered?.isConnected) {
                    ReactTooltip.show(hovered);
                }
            });
        });

        document.addEventListener("mouseover", onMouseOver);
        document.addEventListener("mouseout", onMouseOut);

        return () => {
            stopObserving();
            document.removeEventListener("mouseover", onMouseOver);
            document.removeEventListener("mouseout", onMouseOut);
        };
    }, []);
}

/**
 * The one and only `ReactTooltip` instance, mounted once from `src/index.jsx`.
 *
 * `bodyMode` is what makes a single instance viable: it delegates three listeners to
 * `document.body` and resolves the hovered trigger by walking up from the event target, so
 * triggers that mount later are picked up with no rebinding. Without it react-tooltip binds
 * listeners per target and would need a `ReactTooltip.rebuild()` — and another full-document
 * walk — every time a list re-rendered.
 *
 * react-tooltip renders inline in the DOM, so it inherits any ancestor stacking context or
 * reduced opacity and can end up clipped behind sibling panels. It positions with
 * viewport-fixed coordinates, so a portal on `document.body` keeps it above everything
 * regardless of where the hovered trigger lives in the tree.
 *
 * Per-trigger overrides go on the trigger element as react-tooltip's own data attributes
 * (`data-place`, `data-class`, `data-offset`, ...); those take precedence over these props.
 *
 * NOTE: we deliberately do NOT add Gaia's `ga-tooltip--{place}-center` arrow modifier.
 * react-tooltip flips `place` dynamically (e.g. top -> right near a viewport edge) and
 * updates its own `place-*` class, but the Gaia modifier would stay fixed at the initial
 * `place`, so the two arrows would render in different spots (a stray square). react-tooltip
 * owns the arrow; Gaia only styles the bubble.
 */
export const AppTooltip = () => {
    useKeyboardTriggers();
    useHoverRefresh();

    return ReactDOM.createPortal(
        <ReactTooltip
            id={SHARED_TOOLTIP_ID}
            bodyMode
            role="tooltip"
            className="ga-tooltip"
            place="top"
            globalEventOff="keydown"
            delayHide={300}
            backgroundColor="var(--ga-color-surface-action-hover)"
            textColor="var(--ga-color-text-on-action)"
            arrowColor="var(--ga-color-surface-action-hover)"
            padding="calc(0.4rem * 2)"
            effect="solid"
            getContent={getTooltipContent}
        />,
        document.body
    );
};
